// Unpublished Work © 2020-2023 Deere & Company

import React from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {IntlProvider} from 'react-intl';
import {getLocaleAndCurrency} from '../services/locale-and-currency';
import enAU from '@deere/cap-it-customer-ui.customer-translations/dist/en-AU';
import enCA from '@deere/cap-it-customer-ui.customer-translations/dist/en-CA';
import enNZ from '@deere/cap-it-customer-ui.customer-translations/dist/en-NZ';
import enUS from '@deere/cap-it-customer-ui.customer-translations/dist/en-US';
import frCA from '@deere/cap-it-customer-ui.customer-translations/dist/fr-CA';
import esES from '@deere/cap-it-customer-ui.customer-translations/dist/es-ES';
import esAR from '@deere/cap-it-customer-ui.customer-translations/dist/es-AR';
import daDK from '@deere/cap-it-customer-ui.customer-translations/dist/da-DK';
import deDE from '@deere/cap-it-customer-ui.customer-translations/dist/de-DE';
import elGR from '@deere/cap-it-customer-ui.customer-translations/dist/el-GR';
import fiFI from '@deere/cap-it-customer-ui.customer-translations/dist/fi-FI';
import frFR from '@deere/cap-it-customer-ui.customer-translations/dist/fr-FR';
import huHU from '@deere/cap-it-customer-ui.customer-translations/dist/hu-HU';
import itIT from '@deere/cap-it-customer-ui.customer-translations/dist/it-IT';
import nlNL from '@deere/cap-it-customer-ui.customer-translations/dist/nl-NL';
import noNO from '@deere/cap-it-customer-ui.customer-translations/dist/no-NO';
import plPL from '@deere/cap-it-customer-ui.customer-translations/dist/pl-PL';
import ptBR from '@deere/cap-it-customer-ui.customer-translations/dist/pt-BR';
import roRO from '@deere/cap-it-customer-ui.customer-translations/dist/ro-RO';
import ruRU from '@deere/cap-it-customer-ui.customer-translations/dist/ru-RU';
import svSE from '@deere/cap-it-customer-ui.customer-translations/dist/sv-SE';
import thTH from '@deere/cap-it-customer-ui.customer-translations/dist/th-TH';
import trTR from '@deere/cap-it-customer-ui.customer-translations/dist/tr-TR';
import zhCN from '@deere/cap-it-customer-ui.customer-translations/dist/zh-CN';
import deAT from '@deere/cap-it-customer-ui.customer-translations/dist/de-AT';
import deCH from '@deere/cap-it-customer-ui.customer-translations/dist/de-CH';
import deLU from '@deere/cap-it-customer-ui.customer-translations/dist/de-LU';
import enGB from '@deere/cap-it-customer-ui.customer-translations/dist/en-GB';
import enIE from '@deere/cap-it-customer-ui.customer-translations/dist/en-IE';
import frCH from '@deere/cap-it-customer-ui.customer-translations/dist/fr-CH';
import itCH from '@deere/cap-it-customer-ui.customer-translations/dist/it-CH';
import nlBE from '@deere/cap-it-customer-ui.customer-translations/dist/nl-BE';
import ptPT from '@deere/cap-it-customer-ui.customer-translations/dist/pt-PT';
import ruUA from '@deere/cap-it-customer-ui.customer-translations/dist/ru-UA';

export const UiIntlProvider = (props) => {
    const configuration = useSelector((state) => state.configuration);
    const user = useSelector((state) => state.user);
    const {
        currency,
        locale
    } = getLocaleAndCurrency(configuration, user);
    const localeMap = {
        'da-DK': daDK,
        'de-AT': deAT,
        'de-CH': deCH,
        'de-DE': deDE,
        'de-LU': deLU,
        'el-GR': elGR,
        'en-AU': enAU,
        'en-CA': enCA,
        'en-GB': enGB,
        'en-IE': enIE,
        'en-NZ': enNZ,
        'en-US': enUS,
        'es-AR': esAR,
        'es-ES': esES,
        'fi-FI': fiFI,
        'fr-CA': frCA,
        'fr-CH': frCH,
        'fr-FR': frFR,
        'hu-HU': huHU,
        'it-CH': itCH,
        'it-IT': itIT,
        'nl-BE': nlBE,
        'nl-NL': nlNL,
        'no-NO': noNO,
        'pl-PL': plPL,
        'pt-BR': ptBR,
        'pt-PT': ptPT,
        'ro-RO': roRO,
        'ru-RU': ruRU,
        'ru-UA': ruUA,
        'sv-SE': svSE,
        'th-TH': thTH,
        'tr-TR': trTR,
        'zh-CN': zhCN
    };
    const messages = localeMap[locale];

    const getMatchedLanguage = () => {
        const languageCode = locale.split('-')[0];
        const availableLanguageKeys = Object.keys(localeMap);

        const matchedLanguage = availableLanguageKeys.find((key) => key.split('-')[0] === languageCode);

        return localeMap[matchedLanguage] || enUS;
    };

    return (
        <IntlProvider
            formats={{
                currency,
                locale
            }}
            locale={locale}
            messages={messages || getMatchedLanguage()}
        >
            {props.children}
        </IntlProvider>
    );
};

UiIntlProvider.propTypes = {
    children: PropTypes.node.isRequired
};
